'use client';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DiscoveryProfile } from '@/redux/slices/discoverySlice';
import { getMediaUrl } from '@/constants/media';
import { useAppDispatch } from '@/redux/Store';
import { toggleProfileFavorite } from '@/redux/thunks/dashboardThunks';
import { formatName, nameInitial } from '@/utils/formatName';
import { CreatorRankBadge } from '@/components/CreatorRankBadge';
import { formatPrice } from '@/utils/formatPrice';
import { formatRelativeTime } from '@/utils/formatTime';
import { profileLink } from '@/utils/profileLink';
import './ProfileSearchCard.scss';

interface ProfileSearchCardProps {
  profile: DiscoveryProfile;
}

const ProfileSearchCard: React.FC<ProfileSearchCardProps> = ({ profile }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Drive the heart from server state — the previous local useState reset
  // to false on every mount, so the heart emptied on refresh even when the
  // favorite was actually persisted. Optimistic update happens inside the
  // toggleProfileFavorite thunk via the toggleDiscoveryFavorite reducer.
  const isKlyxd = !!profile.ownership?.iKlyxd;
  // app.profile_favorite.profile_id references nft_profile.id (a UUID).
  // The discover response exposes that UUID as profile.profile.nftProfileId.
  // Sending profile.id (the user UUID) by mistake earned a 404 from
  // /favorites/toggle since the nft_profile lookup never matched.
  const nftProfileId = profile.profile?.nftProfileId
    ? String(profile.profile.nftProfileId)
    : null;

  const handleCardClick = () => {
    navigate(profileLink(profile.username, 'discovery'));
  };

  const handleKlyxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!nftProfileId) return; // creator hasn't minted yet — nothing to favorite
    // Favorite (Klyx'd), not follow — matches the "klyxd" badge + stat
    // above. The bottom heart was wired to toggleFollow by mistake.
    dispatch(toggleProfileFavorite(nftProfileId));
  };

  return (
    <div className="profile-search-card" onClick={handleCardClick}>
      {/* Header with Avatar and Basic Info */}
      <div className="card-header">
        <div className="avatar-section">
          {profile.avatar ? (
            <img src={getMediaUrl(profile.avatar) || undefined} alt={formatName({ displayName: profile.displayName, username: profile.username })} className="avatar" />
          ) : (
            <div className="avatar-placeholder">
              {nameInitial({ displayName: profile.displayName, username: profile.username })}
            </div>
          )}

          {/* Ownership Indicators */}
          <div className="ownership-indicators">
            {profile.ownership?.iOwn && (
              <span className="ownership-badge own" title="You own this profile">
                Own
              </span>
            )}
            {profile.ownership?.iKlyxd && (
              <span className="ownership-badge klyxd" title="You favorited this profile">
                ♥
              </span>
            )}
          </div>
        </div>

        <div className="profile-info">
          {/* Line 1 — displayName OR username (formatName picks one). */}
          <div className="display-name-row">
            <h3 className="display-name">
              {formatName({ displayName: profile.displayName, username: profile.username })}
            </h3>
            <CreatorRankBadge username={profile.username} />
          </div>

          {/* Line 2 — location. */}
          {profile.location && (
            <p className="location-line"><span className="location">{profile.location}</span></p>
          )}

          {/* Line 3 — distance. */}
          {profile.distance && (
            <p className="distance-line"><span className="distance">{profile.distance}</span></p>
          )}
        </div>

        {/* Badges */}
        {(profile.badges.verified || profile.badges.top || profile.badges.trending) && (
          <div className="badges">
            {profile.badges.verified && (
              <span className="badge verified" title="Verified">✓</span>
            )}
            {profile.badges.top && (
              <span className="badge top" title="Top Profile">★</span>
            )}
            {profile.badges.trending && (
              <span className="badge trending" title="Trending">🔥</span>
            )}
          </div>
        )}
      </div>

      {/* Bio */}
      <div className="card-bio">
        <p>{profile.bio || "Not set by user..."}</p>
      </div>

      {/* Interests — matched ones first and highlighted, then the rest */}
      {(profile.interests?.length ?? 0) > 0 && (
        <div className="card-interests">
          {profile.matchedInterests?.map((tag) => (
            <span key={`match-${tag}`} className="interest-chip interest-chip--matched">
              #{tag}
            </span>
          ))}
          {profile.interests
            .filter((tag) => !profile.matchedInterests?.includes(tag))
            .slice(0, Math.max(0, 5 - (profile.matchedInterests?.length ?? 0)))
            .map((tag) => (
              <span key={`other-${tag}`} className="interest-chip">
                #{tag}
              </span>
            ))}
        </div>
      )}
          
      {/* Stats Row */}
      <div className="card-stats">
        <div className="stat">
          <span className="stat-value">{profile.stats.tradeCount}</span>
          <span className="stat-label">Trades</span>
        </div>
        <div className="stat">
          <span className="stat-value">{profile.stats.favoriteCount}</span>
          <span className="stat-label">Klyxd</span>
        </div>
        {profile.profile.isListed && (
          <div className="stat price-stat">
            <span className="stat-value">{formatPrice(profile.profile.priceKlv, { noSymbol: true })}</span>
            <span className="stat-label">KLV</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="card-actions">
        {profile.profile.isListed && !profile.profile.isOwner && (
          <button className="action-btn primary">
            Buy Now
          </button>
        )}
        {profile.profile.isOwner && (
          <span className="owner-label">You Own This</span>
        )}
        {!profile.profile.isListed && !profile.profile.isOwner && (
          <span className="not-listed-label">Not Listed</span>
        )}

        {nftProfileId && (
          <button
            className={`action-btn klyx-btn ${isKlyxd ? 'active' : ''}`}
            onClick={handleKlyxClick}
            title={isKlyxd ? 'Remove favorite' : 'Klyx (favorite)'}
          >
            ♥
          </button>
        )}
      </div>

      {/* Activity Indicator */}
      {profile.stats.lastTradeAt && (
        <div className="activity-indicator">
          <span className="activity-dot"></span>
          <span className="activity-text">
            Active {formatRelativeTime(profile.stats.lastTradeAt)}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProfileSearchCard;
